/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Grounded in docs/agent-context/subsystems/auth-security.md Marketing
 * Constraints.
 *
 * CRITICAL role list (verified 2026-04-15 against AppRole type in
 * src/lib/business-logic/organizations/authorization.ts):
 *
 *   OWNER, ADMIN, MANAGER, SALESMAN, ESTIMATOR, CREW_LEADER, LABORER, VIEWER
 *
 * The prior version of this page listed "Owner, Admin, Manager, Estimator,
 * PM, Crew Leader, Laborer, Guest" — wrong in three places:
 *   - "PM" is NOT a role in CC. Project managers are MANAGER or ESTIMATOR.
 *   - "Guest" is latent in lowercase USER_ROLES only, not in AppRole, and
 *     normalizeRole() collapses it to VIEWER. Do NOT market GUEST.
 *   - Missing SALESMAN, which is a real AppRole.
 *
 * Do NOT market "every route is role-gated" — ~77% of routes have no
 * explicit RBAC check per the audit. Market the parts that ARE gated
 * (data-write operations, AI tools, sensitive pages) honestly.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  ShieldCheck,
  UserPlus,
  Users,
  Building2,
  Briefcase,
  Calculator,
  Lock,
  Layers,
  Crown,
  Wrench,
  Zap,
  Bot,
  Eye,
  DollarSign,
  FileText,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Team & Permissions",
  title: "Put the right people",
  titleHighlight: "on the right work.",
  description:
    "Use role-based access built for contractor teams so sales, estimating, field, and leadership can collaborate without stepping on each other.",

  featureGrid: {
    title: "Team, trust, and tenant isolation",
    items: [
      {
        icon: Crown,
        title: "8 Production Roles",
        description:
          "Owner, Admin, Manager, Salesman, Estimator, Crew Leader, Laborer, Viewer. Designed for how contracting companies actually operate — not a generic SaaS hierarchy.",
      },
      {
        icon: UserPlus,
        title: "Invite by Email",
        description:
          "Invite a team member by email and assign their role in one step so onboarding stays fast and controlled.",
      },
      {
        icon: Layers,
        title: "3-Layer Org Isolation",
        description:
          "Protect organization data with multiple enforcement layers so teams can trust what they see.",
      },
      {
        icon: Lock,
        title: "Role-Gated Writes",
        description:
          "Limit sensitive write actions by role so financial and operational records stay protected.",
      },
      {
        icon: Bot,
        title: "AI Tools Respect Roles",
        description:
          "AI assistance adapts by role so each user sees tools aligned with their responsibilities.",
      },
      {
        icon: Zap,
        title: "Real-Time Presence",
        description:
          "See who else is viewing an estimate right now so teams can coordinate edits and avoid stepping on each other.",
      },
      {
        icon: Wrench,
        title: "Field Role Differentiation",
        description:
          "Crew Leaders can approve time entries. Laborers can clock in and out but cannot approve their own time.",
      },
      {
        icon: Building2,
        title: "Division Scoping",
        description:
          "Business-plan orgs can scope users and estimates to divisions. Residential doesn't see commercial's pipeline unless you want them to.",
      },
      {
        icon: ShieldCheck,
        title: "Secure by Default",
        description:
          "Core security protections are built in from day one so teams can work confidently without custom setup.",
      },
    ],
  },

  workflow: {
    title: "How a contractor team comes online in CC",
    description:
      "A clear onboarding flow from account setup to productive team collaboration.",
    steps: [
      {
        label: "Create",
        title: "Your org is created on payment",
        description:
          "Start with a clean organization setup so ownership and accountability are clear from day one.",
        details: [
          "Organization setup begins after billing confirmation",
          "The first payer is established as owner automatically",
          "Sign-in identity and app user profile stay aligned",
          "Eligible invited users are connected to the org with less friction",
        ],
      },
      {
        label: "Invite",
        title: "Send invitations with role assignment",
        description:
          "Invite teammates with pre-assigned roles so they land in the correct permissions context immediately.",
        details: [
          "Email invitation with role pre-assigned",
          "Secure sign-in flow for invited users",
          "Low-friction join flow for eligible invitees",
          "An organization must keep at least one owner before demotion or deletion is allowed",
          "Re-invite and revoke supported",
        ],
      },
      {
        label: "Assign",
        title: "Match the role to the job",
        description:
          "Map each user to the role that matches their day-to-day work so teams move faster with fewer permission mistakes.",
        details: [
          "Owner — company-wide, billing, destructive actions",
          "Admin — system-wide operational access",
          "Manager — projects, financials, team coordination",
          "Salesman — pipeline, clients, estimates, proposals",
          "Estimator — cost database, estimates, takeoffs",
          "Crew Leader — time approval, dispatch, field work",
          "Laborer — clock in/out, view assigned tasks",
          "Viewer — read-only access where permitted",
        ],
      },
      {
        label: "Enforce",
        title: "Three layers protecting your data",
        description:
          "Apply layered safeguards to keep tenant boundaries and sensitive operations protected as teams scale.",
        details: [
          "Application safeguards keep work scoped to the right organization",
          "Request-level checks keep users in the right tenant context",
          "Database protections add a final safety layer on core tables",
          "Organization scoping is enforced on data read and write paths",
          "Data stays scoped to the right organization across the platform",
        ],
      },
      {
        label: "Collaborate",
        title: "Work at the same time without stepping on each other",
        description:
          "Use live collaboration cues and lock protections so shared records stay reliable when teams work at the same time.",
        details: [
          "Live presence indicators on estimates",
          "Estimate lock after proposal send",
          "Notifications for proposal views, invoice payments, and deadlines",
          "Activity timelines on key records across the platform",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: Users,
      title: "Eight Roles Designed for Contractor Teams",
      description:
        "Use role definitions that match real field and office responsibilities so permissions reflect how work actually gets done.",
      bullets: [
        "Owner — company-wide access, billing, team management",
        "Admin — org operations and settings control",
        "Manager — project management, financials, cross-team coordination",
        "Salesman — client pipeline, estimates, proposals",
        "Estimator — cost database, estimate builder, takeoffs",
        "Crew Leader — dispatch, time approval, field operations",
        "Laborer — clock-in, assigned tasks, limited write",
        "Viewer — read-only access where permitted",
      ],
    },
    {
      icon: Layers,
      title: "Layered Organization Isolation",
      description:
        "Keep tenant boundaries strong with multiple safeguards so data integrity does not depend on a single protection point.",
      bullets: [
        "Application safeguards enforce organization scoping",
        "Request checks keep users in the correct tenant context",
        "Database protections add another isolation layer",
        "Sensitive operations are protected by multiple checks",
        "Data remains separated across organizations",
        "Isolation protections are enforced in production",
      ],
    },
    {
      icon: Bot,
      title: "Role-Aware AI Assistance",
      description:
        "Keep AI assistance useful and safe by matching available actions to each user’s permission scope.",
      bullets: [
        "Tool availability follows role permissions by work type",
        "Write actions require human confirmation before committing",
        "Hank never silently modifies data — every change is reviewed",
        "Permission scope updates are reflected quickly for AI tool access",
        "Org-scoped data access for assistant workflows",
      ],
    },
    {
      icon: ShieldCheck,
      title: "Collaboration Safety for Shared Records",
      description:
        "Protect high-stakes records during team collaboration with lock behavior and visibility into concurrent activity.",
      bullets: [
        "Estimate locks automatically on proposal send",
        "Lock protections enforced across critical write paths",
        "Realtime presence on estimate detail pages",
        "Self-approval blocked on time entries (compliance win)",
        "Audit trails on every stage transition",
      ],
    },
  ],

  connections: {
    title: "How team and permissions connect to the rest of CC",
    items: [
      {
        icon: Briefcase,
        flow: "Clients → Assigned Owner → Estimate Owner",
        description:
          "A client's assigned owner flows into estimate ownership automatically. Salespeople and estimators keep track of their own work without manual reassignment.",
      },
      {
        icon: Calculator,
        flow: "Role → AI Toolset",
        description:
          "Hank's available tools are filtered per role. Laborers see field tools. Managers see financial tools. Salespeople see pipeline tools. Same assistant, different hands.",
      },
      {
        icon: Eye,
        flow: "Proposal Sent → Estimate Locked",
        description:
          "Sending a proposal locks the underlying estimate so teams cannot silently rewrite sent numbers.",
      },
      {
        icon: Building2,
        flow: "Division → User Scope → Data Scope",
        description:
          "On Business plans, users are scoped to divisions. Division assignment flows into estimate visibility, project filtering, and budget access automatically.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "Project Management",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    {
      label: "Proposals & Client Portal",
      href: "/features/proposals-portal",
      icon: FileText,
    },
    {
      label: "CRM",
      href: "/features/crm",
      icon: Users,
    },
    {
      label: "Documents",
      href: "/features/documents",
      icon: Layers,
    },
  ],
};

export function CollaborationPageClient() {
  return <FeaturePageTemplate config={config} />;
}
